import { Fragment, useEffect, useMemo, useRef, useState } from 'react';

import {
  ChakraBox,
  ChakraFlex,
  ChakraHStack,
  ChatUser,
} from '@equip.health/ui';
import { GroupChannel } from '@sendbird/chat/groupChannel';
import { ChannelProvider } from '@sendbird/uikit-react/Channel/context';
import { useHistory, useLocation } from 'react-router-dom';
import { useFlags } from 'launchdarkly-react-client-sdk';

import OverviewLoading from '~/components/common/OverviewLoading';
import { ApiCommand } from '~/lib/Api';
import { NEW_MESSAGE } from '~/lib/constants/chat.constants';
import urlConstants from '~/lib/constants/url.constants';
import { useGlobalContext } from '~/lib/contexts/GlobalContext';
import { useApi, useS3FileUpload } from '~/lib/hooks';
import useChat from '~/lib/hooks/chat/useChat';
import {
  ChatMessage,
  FileAttachment,
  MessageAttachment,
} from '~/lib/types/chat';
import ChatUtil, {
  getMentionsBoxReplyPlacholderText,
  isExternalChannel,
} from '~/lib/util/chat/chat.utils';
import {
  ChatChannelTab,
  getChannelName,
} from '~/lib/util/chat/chatChannel.util';
import {
  getChannelTabIndexToChannelsMapper,
  getDefaultChannel,
  onChannelTabChangeHandler,
} from '~/lib/util/patientChannels.util';
import { getValueFromQueryParams } from '~/lib/utils';
import { Path } from '~/routes/constants';

import AttachmentViewer from './AttachmentViewer';
import ChannelListPanel from './ChannelListPanel';
import ChatOutOfOfficeActionBarList from './ChatOutOfOfficeActionBarList';
import EmptyChannel from './EmptyChannel';
import MentionsBox from './MentionsBox';
import MessagesHeader from './MessagesHeader';
import PatientMyMessagesPanel from './PatientMyMessagesPanel';
import { useSendbirdReadyContext } from '~/lib/contexts/SendbirdReadyContext';
import { ErrorBoundary } from 'react-error-boundary';
import ErrorMessagesFallback from './ErrorMessagesFallback';
import { useDatadog } from '~/lib/hooks/useDatadog';
import { PATIENT_MESSAGES_SIDEBAR_WIDTH } from '~/lib/constants/patients.constants';

const { getAttachmentUploadUrl: getAttachmentUploadPathUrl } =
  urlConstants.clinical_data;

const idPrefix = 'messages';
const idPostfix = 'tab';

interface PatientMyChannelsProps {
  patientFirstName: string;
  patientId: string;
  selectedChannelType: string;
  channelTypeOptions: UniversalSelectOption[];
  onChannelTypeSelect: (newChannelType: string) => void;
  universalStatusCode?: string;
}

const emptyMessageAttachment: MessageAttachment = {
  file: null,
  mentionedUserIds: [],
  parentMessageId: 0,
  thread: [],
};

const PatientMyChannels = ({
  patientFirstName,
  patientId,
  selectedChannelType,
  channelTypeOptions,
  onChannelTypeSelect,
  universalStatusCode,
}: PatientMyChannelsProps) => {
  const { currentUser } = useGlobalContext();
  const { sdk: sendBirdInstance } = useSendbirdReadyContext();
  const [selectedChannel, setSelectedChannel] = useState<GroupChannel>(null);
  const [filesToSend, setFilesToSend] = useState<MessageAttachment>(
    emptyMessageAttachment,
  );
  const [isTopicFileMessage, setIsTopicFileMessage] = useState<boolean>(false);
  const [outgoingFileUrl, setOutgoingFileUrl] = useState<string>(null);
  const [isSendingMessage, setIsSendingMessage] = useState<boolean>(false);
  const [isSendingReply, setIsSendingReply] = useState<boolean>(false);
  const [channelTabIndex, setChannelTabIndex] = useState<number>(0);
  const textBoxRef = useRef<HTMLInputElement>(null);
  const replyTextBoxRef = useRef<HTMLInputElement>(null);

  const { maudProviderChatMessagesPrototype: isMessageThreadingEnabled } =
    useFlags<LDFlags>();

  const { logError } = useDatadog();

  const {
    channels,
    chatMessages,
    getReplies,
    hasMoreMessages,
    isChannelsLoading,
    isChannelReadOnly,
    isGroupChannel,
    loadChannels,
    loadMessages,
    markMessagesAsRead,
    removeMessage,
    sendFileMessage,
    sendMessageReply,
    sendUserMessage,
  } = useChat({
    channel: selectedChannel,
    chatUserId: currentUser.userId,
    onChannelUpdated: (channel) => setSelectedChannel(channel), // TODO: causes a circular dependency in useChat
    patientId,
    universalStatusCode,
  });
  const { sendRequest: getAttachmentUploadUrlSendRequest } = useApi<{
    data?: { attachmentUploadUrl: string };
  }>();

  const { sendRequest: sendAttachmentUploadRequest, isFileUploaded } =
    useS3FileUpload();

  const messageInputWrapperRef = useRef<HTMLDivElement>(null);
  const messageListPanelRef = useRef<HTMLDivElement>(null);

  const { search } = useLocation();
  const channelUrl = getValueFromQueryParams(search, 'channelUrl');
  const history = useHistory();

  const getUnreadCount = (channels: GroupChannel[]): number =>
    channels.reduce(
      (count: number, thisChannel: GroupChannel) =>
        thisChannel.unreadMentionCount + thisChannel.unreadMessageCount + count,
      0,
    );

  const channelTabIndexToChannelsMapper =
    getChannelTabIndexToChannelsMapper(channels);

  const selectedTabChannels = useMemo(
    () => channelTabIndexToChannelsMapper[channelTabIndex],
    [channels, channelTabIndex],
  );

  // EQ-10793 - input focus after the message is sent
  //  Must keep message and reply actions separate.
  useEffect(() => {
    if (!isSendingMessage) {
      if (textBoxRef.current) textBoxRef.current.focus();
    }
  }, [isSendingMessage]);
  useEffect(() => {
    if (!isSendingReply) {
      if (replyTextBoxRef.current) replyTextBoxRef.current.focus();
    }
  }, [isSendingReply]);

  useEffect(() => {
    //If user clicks chat notification card from notification widget, and if user was in messages tab > internal tab already
    //We set channel tab to external first and select proper channel

    //If user clicks chat notification card from notification widget, and if user was not in message tab
    //We find correct channel on Interanl channel tab first, but if channalUrl is mismatched,
    //We select channel on External channel tab again
    if (
      channels.length > 0 &&
      channelUrl &&
      isExternalChannel(
        channels.find((channel: GroupChannel) => channel.url === channelUrl)
          ?.url,
      ) &&
      channelTabIndex === ChatChannelTab.INTERNAL
    ) {
      setChannelTabIndex(ChatChannelTab.EXTERNAL);
      setSelectedChannel(null);
    } else if (!selectedChannel && selectedTabChannels?.length > 0) {
      const newSelectedChannel =
        channels.find((channel) => channel.url === channelUrl) ??
        getDefaultChannel(selectedTabChannels);

      setSelectedChannel(newSelectedChannel);
      setFilesToSend(emptyMessageAttachment);

      if (isMessageThreadingEnabled && newSelectedChannel) {
        markMessagesAsRead(newSelectedChannel.url);
      }
    }
  }, [selectedTabChannels, channelUrl, markMessagesAsRead]);

  useEffect(() => {
    if (sendBirdInstance) loadChannels();
  }, [sendBirdInstance]);

  const fileMessages = useMemo<ChatMessage[]>(() => {
    const output = [];
    chatMessages?.forEach((message) => {
      if (message.isFileMessage && !message.isRemoved) {
        output.push(message);
      }

      if (message.thread?.length > 0) {
        message.thread?.forEach((reply) => {
          if (reply.isFileMessage && !reply.isRemoved) {
            output.push(reply);
          }
        });
      }
    });
    return output;
  }, [chatMessages]);

  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isFileUploaded && outgoingFileUrl) {
      sendFileMessage(
        filesToSend.file,
        outgoingFileUrl,
        filesToSend.parentMessageId,
        filesToSend.mentionedUserIds,
        () => {
          if (filesToSend.parentMessageId === 0 && textBoxRef.current)
            textBoxRef.current.focus();
          else if (replyTextBoxRef.current) replyTextBoxRef.current.focus();
          setFilesToSend(emptyMessageAttachment);
          if (isTopicFileMessage) setIsTopicFileMessage(!isTopicFileMessage);
          setIsSendingMessage(false);
          setIsSendingReply(false);
        },
      );
    }
  }, [isFileUploaded, outgoingFileUrl]);

  const currentSelectedChannel = useMemo(() => {
    if (textBoxRef.current) textBoxRef.current.focus();
    return (
      selectedTabChannels?.find(
        (channel) => channel.url === selectedChannel?.url,
      ) ?? selectedChannel
    );
  }, [channelTabIndex, selectedChannel]);

  const sendNewFileMessage = (attachment: FileAttachment) => {
    if (attachment) {
      getAttachmentUploadUrlSendRequest({
        callback: ({ data }) => {
          setOutgoingFileUrl(data.attachmentUploadUrl);
          sendAttachmentUploadRequest({
            file: attachment.file,
            url: data.attachmentUploadUrl,
          });
        },
        command: ApiCommand.POST,
        options: {
          channelId: selectedChannel.url,
          documentExternalId: attachment.name.replace(
            `-${attachment.file.name}`,
            '',
          ),
          fileName: attachment.file.name,
          patientExternalId: patientId,
        },
        url: getAttachmentUploadPathUrl,
      });
    } else {
      // UI is needed to show errors while sending file messages.
      logError(
        new Error(
          'Unable to send file message. Did not find any valid attachments.',
        ),
      );
      setIsSendingMessage(false);
      setIsSendingReply(false);
    }
  };

  const sendNewMessage = (
    message: string | FileAttachment,
    mentionedUserIds: string[],
    parentMessageId: number,
    thread: ChatMessage[],
    isUpdateStatus: boolean,
  ) => {
    if (typeof message !== 'string') {
      const newValue: MessageAttachment = {
        file: message,
        mentionedUserIds: ChatUtil.expandHere(
          (currentSelectedChannel?.members ?? []).map((x) => x.userId),
          mentionedUserIds,
        ),
        parentMessageId,
        thread,
      };
      setFilesToSend(newValue);

      sendNewFileMessage(message);
    } else {
      const newChatMessage = {
        ...NEW_MESSAGE,

        channelUrl: currentSelectedChannel.url,
        mentionedUserIds: ChatUtil.expandHere(
          (currentSelectedChannel?.members ?? []).map((x) => x.userId),
          mentionedUserIds,
        ),
        message,
        parentMessageId,
        thread,
      } as ChatMessage;

      if (parentMessageId === 0) {
        sendUserMessage(newChatMessage, () => {
          messageListPanelRef.current?.scrollTo({ behavior: 'smooth', top: 0 });
          if (isUpdateStatus) setIsSendingMessage(false);
        });
      } else {
        sendMessageReply(newChatMessage, () => {
          if (isUpdateStatus) setIsSendingReply(false);
        });
      }
    }
  };

  const handleSendMessage = (newMessage: ChatMessage) => {
    setIsSendingMessage(newMessage.parentMessageId === 0);
    setIsSendingReply(newMessage.parentMessageId > 0);

    if (newMessage.message) {
      sendNewMessage(
        newMessage.message,
        newMessage.mentionedUserIds ?? [],
        newMessage.parentMessageId ?? 0,
        newMessage.thread ?? [],
        !newMessage.file,
      );
    }

    if (newMessage.file) {
      sendNewMessage(
        newMessage.file,
        newMessage.mentionedUserIds ?? [],
        newMessage.parentMessageId ?? 0,
        newMessage.thread ?? [],
        true,
      );
    }
  };

  /**
   *
   * @param messageId: Message Id (ideal values 0 or greater)
   * @param replyId (Optional and cannot be 0):
   */
  const handleRemovedMessage = (messageId: number, replyId?: number) => {
    removeMessage(messageId, replyId);
  };

  const onChannelTabChange = (tab: number) => {
    onChannelTabChangeHandler({
      channels,
      channelTabIndex,
      history,
      patientId,
      setChannelTabIndex,
      setSelectedChannel,
      tab,
    });
  };

  if (isChannelsLoading) return <OverviewLoading />;

  if (channels?.length === 0)
    return (
      <EmptyChannel
        channelTypeOptions={channelTypeOptions}
        id="empty-channel"
        onChannelTypeSelect={onChannelTypeSelect}
        patientId={patientId}
        selectedChannelType={selectedChannelType}
      />
    );

  const errorFallback = (props) => (
    <ChakraBox textAlign="center" width="100%">
      <ErrorMessagesFallback {...props} />
    </ChakraBox>
  );

  return (
    <Fragment>
      <ChakraFlex
        alignItems="stretch"
        direction="column"
        id={`${idPrefix}-messages-tab-container__${currentUser.userId}__${idPostfix}`}
        marginBottom="2px"
        paddingRight="16px"
        width="100%"
      >
        <MessagesHeader
          channelAttachments={fileMessages}
          channelMembers={currentSelectedChannel?.members as ChatUser[]}
          channelTabIndex={channelTabIndex}
          channelTitle={getChannelName(currentSelectedChannel)}
          channelTypeOptions={channelTypeOptions}
          id={`${idPrefix}-messages-header__${currentUser.userId}__${idPostfix}`}
          isChannelReadOnly={isChannelReadOnly}
          onChannelTabChange={onChannelTabChange}
          onChannelTypeSelect={onChannelTypeSelect}
          onMessageRemoved={handleRemovedMessage}
          selectedChannelType={selectedChannelType}
          unreadCount={{
            [ChatChannelTab.EXTERNAL]: getUnreadCount(
              channelTabIndexToChannelsMapper[ChatChannelTab.EXTERNAL],
            ),
            [ChatChannelTab.INTERNAL]: getUnreadCount(
              channelTabIndexToChannelsMapper[ChatChannelTab.INTERNAL],
            ),
          }}
        />
      </ChakraFlex>
      <ChakraFlex
        flex={1}
        gridGap="48px"
        height="100%"
        id={`${idPrefix}-channels-container__${currentUser.userId}__${idPostfix}`}
        overflowY="hidden"
        ref={containerRef}
        width="100%"
      >
        <ChakraBox
          flex="0 0 auto"
          id={`${idPrefix}-channels-wrapper__${currentUser.userId}__${idPostfix}`}
          overflowY="auto"
          width={PATIENT_MESSAGES_SIDEBAR_WIDTH}
        >
          <ChannelListPanel
            channels={selectedTabChannels ?? []}
            id={`${idPrefix}-channels__${currentUser.userId}__${idPostfix}`}
            onChannelSelected={(url) => {
              if (currentSelectedChannel?.url !== url) {
                history.push(
                  `${Path.MAUD_PATIENTS}/${patientId}/messages?channelUrl=${url}`,
                );
                setFilesToSend(emptyMessageAttachment);
                const newChannel = selectedTabChannels?.find(
                  (channel: GroupChannel) => channel.url === url,
                );
                setSelectedChannel(newChannel);

                if (isMessageThreadingEnabled && newChannel) {
                  markMessagesAsRead(url);
                }
              }
            }}
            patientName={patientFirstName}
            selectedChannelUrl={currentSelectedChannel?.url}
          />
        </ChakraBox>
        <ChakraFlex
          direction="column"
          flex={1}
          id={`${idPrefix}-messages-container__${currentUser.userId}__${idPostfix}`}
        >
          <ChakraBox
            data-testid={`${idPrefix}-messages-wrapper__${currentUser.userId}__${idPostfix}`}
            height="100%"
            id={`${idPrefix}-messages-wrapper__${currentUser.userId}__${idPostfix}`}
            overflowY="auto"
            position="relative"
            width="100%"
          >
            {selectedChannel && (
              <ChannelProvider
                channelUrl={selectedChannel?.url}
                disableMarkAsRead
              >
                <ErrorBoundary
                  fallbackRender={errorFallback}
                  onReset={() => {
                    setSelectedChannel(null);
                    history.push(`${Path.MAUD_PATIENTS}/${patientId}/messages`);
                  }}
                >
                  <PatientMyMessagesPanel
                    channel={selectedChannel}
                    chatUserId={currentUser.userId}
                    hasMoreMessages={hasMoreMessages}
                    id={`${idPrefix}-messages-list__${currentUser.userId}__${idPostfix}`}
                    isChannelReadOnly={isChannelReadOnly}
                    isGroupChannel={isGroupChannel}
                    isMessageSent={isSendingReply}
                    loadMoreMessages={() => loadMessages()}
                    markMessagesAsRead={() =>
                      markMessagesAsRead(currentSelectedChannel?.url)
                    }
                    messages={chatMessages?.filter(
                      (message) => !message.isRemoved,
                    )}
                    onInputFocus={(inputTextRef) => {
                      replyTextBoxRef.current = inputTextRef.current;
                    }}
                    onMessageRemoved={handleRemovedMessage}
                    onRepliesExpanded={(selectedMessage) =>
                      getReplies(selectedMessage)
                    }
                    onSendMessage={handleSendMessage}
                    patientId={patientId}
                    ref={messageListPanelRef}
                  />
                </ErrorBoundary>
              </ChannelProvider>
            )}
          </ChakraBox>
          <ChakraHStack flexDirection="column" gap={0} marginBottom="8px">
            <ChatOutOfOfficeActionBarList selectedChannel={selectedChannel} />
          </ChakraHStack>
          {!isChannelReadOnly && (
            <ChakraBox
              backgroundColor="white"
              borderColor="neutral.line"
              borderRadius="16px"
              borderWidth="1px"
              bottom="0"
              id={`${idPrefix}-messages-mentions-box-container__${currentUser.userId}__${idPostfix}`}
              padding="8px"
              position="sticky"
              ref={messageInputWrapperRef}
              width="100%"
            >
              <MentionsBox
                channelUrl={selectedChannel?.url}
                id={selectedChannel?.url}
                isMessageSent={isSendingMessage}
                members={selectedChannel?.members as ChatUser[]}
                onEnterPressed={handleSendMessage}
                onFileSelected={(selectedFile) => {
                  setIsTopicFileMessage(true);
                  const value: MessageAttachment = {
                    file: selectedFile,
                    mentionedUserIds: [],
                    parentMessageId: 0,
                    thread: [],
                  };
                  setFilesToSend(value);
                }}
                onFocus={(inputTextRef) => {
                  textBoxRef.current = inputTextRef.current;
                }}
                placeholder={getMentionsBoxReplyPlacholderText(false)}
                updatedAttachment={filesToSend?.file}
              />
              {filesToSend?.file && isTopicFileMessage && (
                <AttachmentViewer
                  attachments={[filesToSend?.file?.file]}
                  id={selectedChannel?.url}
                  isLoading={isSendingMessage}
                  onRemove={() => {
                    setFilesToSend(null);
                  }}
                />
              )}
            </ChakraBox>
          )}
        </ChakraFlex>
      </ChakraFlex>
    </Fragment>
  );
};

export default PatientMyChannels;
